/**
 * Profile Registry
 *
 * Per-domain storage for view profiles. Each domain registers its profiles
 * at bootstrap (e.g. `registerNetworkDomain()` → `registerProfile('network', ...)`).
 * UI code then lists profiles for the current diagram type and switches
 * between them via `useViewProfileStore`.
 */

import type { ScoringProfile } from './ScoringEngine';

const profiles = new Map<string, ScoringProfile>();
const profilesByDomain = new Map<string, ScoringProfile[]>();

/**
 * Register a profile. Called at domain bootstrap. Idempotent: if a profile
 * with the same id exists, it's replaced.
 */
export function registerProfile(profile: ScoringProfile): void {
  profiles.set(profile.id, profile);
  const list = profilesByDomain.get(profile.domain) ?? [];
  const filtered = list.filter((p) => p.id !== profile.id);
  filtered.push(profile);
  profilesByDomain.set(profile.domain, filtered);
}

/** Look up a profile by id across all domains. */
export function getProfile(id: string): ScoringProfile | undefined {
  return profiles.get(id);
}

/** List all profiles registered for a domain. */
export function getProfilesForDomain(domain: string): ScoringProfile[] {
  return profilesByDomain.get(domain) ?? [];
}

/** Get the default profile for a domain (first one flagged `default: true`). */
export function getDefaultProfile(domain: string): ScoringProfile | undefined {
  const list = profilesByDomain.get(domain) ?? [];
  return list.find((p) => p.default) ?? list[0];
}

/** Clear all profiles — useful in tests. */
export function resetProfileRegistry(): void {
  profiles.clear();
  profilesByDomain.clear();
}
